import { catalogOpsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  BatchExclusionRequest,
  BatchExclusionResponse,
  BatchCarryover,
  BatchExclusionUndoResponse,
  BatchRetryRequest,
  BatchRetryResponse,
  BatchRunProductPage,
  BatchRunProductsQuery,
  BatchRuns,
  NeedsAttention,
  ProductAttempts,
} from '../types/batch-retry.api.types';

/** GET jobs/{id}/runs — the batch as root run + retries, with server-decided actions. */
export async function getBatchRuns(jobId: string): Promise<BatchRuns> {
  return catalogOpsClient.get(endpoints.catalogOperations.batchRuns(jobId)).json<BatchRuns>();
}

/**
 * GET jobs/{id}/carryover — products this batch took from earlier batches / earlier left to later
 * ones, plus the balance. 404 = a backend that predates the feature: null, nothing to render.
 */
export async function getBatchCarryover(jobId: string): Promise<BatchCarryover | null> {
  try {
    return await catalogOpsClient
      .get(endpoints.catalogOperations.batchCarryover(jobId))
      .json<BatchCarryover>();
  } catch (error) {
    if ((error as { response?: { status?: number } }).response?.status === 404) return null;
    throw error;
  }
}

/** GET jobs/{id}/runs/{runId}/products — one retry run's products; nullish query values dropped. */
export async function getBatchRunProducts(
  jobId: string,
  runId: string,
  query: BatchRunProductsQuery,
): Promise<BatchRunProductPage> {
  const searchParams: Record<string, string | number> = {};
  if (query.outcome) searchParams.outcome = query.outcome;
  if (query.source) searchParams.source = query.source;
  if (query.batchId) searchParams.batchId = query.batchId;
  if (query.q) searchParams.q = query.q;
  if (query.page !== undefined) searchParams.page = query.page;
  if (query.size !== undefined) searchParams.size = query.size;
  return catalogOpsClient
    .get(endpoints.catalogOperations.batchRunProducts(jobId, runId), { searchParams })
    .json<BatchRunProductPage>();
}

/** GET jobs/{id}/products/{productId}/attempts — every attempt at one product. */
export async function getProductAttempts(
  jobId: string,
  productId: string,
): Promise<ProductAttempts> {
  return catalogOpsClient
    .get(endpoints.catalogOperations.batchProductAttempts(jobId, productId))
    .json<ProductAttempts>();
}

/** GET jobs/{id}/needs-attention — what still needs a person, classified. */
export async function getBatchNeedsAttention(jobId: string): Promise<NeedsAttention> {
  return catalogOpsClient
    .get(endpoints.catalogOperations.batchNeedsAttention(jobId))
    .json<NeedsAttention>();
}

/** POST jobs/{id}/retries — start a retry child (202). 409 carries a human-readable message. */
export async function startBatchRetry(
  jobId: string,
  body: BatchRetryRequest,
): Promise<BatchRetryResponse> {
  return catalogOpsClient
    .post(endpoints.catalogOperations.batchRetries(jobId), { json: body })
    .json<BatchRetryResponse>();
}

/** POST jobs/{id}/exclusions — exclude every product still not enriched. 409 carries the reason. */
export async function excludeRemaining(
  jobId: string,
  body: BatchExclusionRequest,
): Promise<BatchExclusionResponse> {
  return catalogOpsClient
    .post(endpoints.catalogOperations.batchExclusions(jobId), { json: body })
    .json<BatchExclusionResponse>();
}

/** DELETE jobs/{id}/exclusions — undo the exclusion (409 once delivery started). */
export async function undoExclusion(jobId: string): Promise<BatchExclusionUndoResponse> {
  return catalogOpsClient
    .delete(endpoints.catalogOperations.batchExclusions(jobId))
    .json<BatchExclusionUndoResponse>();
}
